import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import api from "../../api";
import ParentWrongQuestionsReviewDialog from "../../components/parent/ParentWrongQuestionsReviewDialog";

/**
 * @param {Array<Record<string, unknown>>} weakAreas
 */
function groupWeakAreasByTopic(weakAreas) {
  const map = new Map();
  for (const w of weakAreas || []) {
    const tid = w.course_topic_id ?? 0;
    if (!map.has(tid)) {
      map.set(tid, {
        topicId: tid,
        topicName: (w.course_topic_name && String(w.course_topic_name)) || "Topic",
        rows: [],
      });
    }
    map.get(tid).rows.push(w);
  }

  return Array.from(map.values())
    .map((g) => {
      const topicLevel = g.rows.filter((r) => !r.course_sub_topic_id);
      const subtopics = g.rows
        .filter((r) => r.course_sub_topic_id)
        .sort((a, b) =>
          String(a.course_sub_topic_name || "").localeCompare(String(b.course_sub_topic_name || ""))
        );
      return { ...g, topicLevel, subtopics };
    })
    .sort((a, b) => a.topicName.localeCompare(b.topicName));
}

const scoreChipColor = (score) => {
  const n = Number(score) || 0;
  if (n >= 50) return "warning";
  return "error";
};

const SchoolReadinessDetail = () => {
  const { studentId, targetSchoolId } = useParams();
  const location = useLocation();
  const navState = location.state || {};
  const stateStudentName = typeof navState.studentName === "string" ? navState.studentName : "";
  const stateSchoolName =
    typeof navState.targetSchoolName === "string" ? navState.targetSchoolName : "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [reviewData, setReviewData] = useState(null);
  const [reviewContextSubtitle, setReviewContextSubtitle] = useState("");

  const openWrongQuestionsReview = useCallback(
    async (subject, group, subtopicWeakRow) => {
      const topicId = Number(group.topicId);
      if (!studentId || !targetSchoolId || !subject?.id || !topicId) {
        setReviewData(null);
        setReviewError("Missing topic or student.");
        setReviewContextSubtitle("");
        setReviewOpen(true);
        return;
      }
      const subtopicId = subtopicWeakRow?.course_sub_topic_id
        ? Number(subtopicWeakRow.course_sub_topic_id)
        : 0;
      const subtopicLabel = subtopicWeakRow
        ? String(subtopicWeakRow.course_sub_topic_name || subtopicWeakRow.display_path || "").trim()
        : "";

      const schoolLabel = payload?.target_school_name || stateSchoolName || "Target school";
      const ctx = [
        `School : ${schoolLabel}`,
        subject.name,
        `Topic : ${group.topicName}`,
        subtopicLabel ? `Subtopic : ${subtopicLabel}` : null,
      ]
        .filter(Boolean)
        .join(" · ");

      setReviewOpen(true);
      setReviewLoading(true);
      setReviewError("");
      setReviewData(null);
      setReviewContextSubtitle(ctx);

      try {
        const params = {
          subject_id: subject.id,
          course_topic_id: topicId,
        };
        if (subtopicId > 0) {
          params.course_sub_topic_id = subtopicId;
        }
        const res = await api.get(
          `parent/student/${studentId}/target-school/${targetSchoolId}/readiness-wrong-questions`,
          { params }
        );
        if (res.data?.success && res.data?.data) {
          setReviewData(res.data.data);
        } else {
          setReviewError(res.data?.message || "Could not load wrong questions.");
        }
      } catch (err) {
        console.error(err);
        setReviewError(err.response?.data?.message || "Failed to load wrong questions.");
      } finally {
        setReviewLoading(false);
      }
    },
    [studentId, targetSchoolId, payload?.target_school_name, stateSchoolName]
  );

  useEffect(() => {
    if (!studentId || !targetSchoolId) {
      setError("Missing student or school.");
      setLoading(false);
      return;
    }
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(
          `parent/student/${studentId}/target-school/${targetSchoolId}/readiness-weak-areas`
        );
        if (res.data?.success && res.data?.data) {
          setPayload(res.data.data);
        } else {
          setPayload(null);
          setError(res.data?.message || "Could not load readiness details.");
        }
      } catch (err) {
        console.error(err);
        setPayload(null);
        setError(err.response?.data?.message || "Failed to load readiness details.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [studentId, targetSchoolId]);

  const thresholdLabel = useMemo(() => {
    const t = payload?.threshold;
    return typeof t === "number" && t > 0 ? t : 80;
  }, [payload]);

  const schoolTitle = payload?.target_school_name || stateSchoolName || "Target school";
  const headerStudent = stateStudentName || null;

  return (
    <Box
      sx={{
        pt: { xs: 2, md: 3 },
        px: { xs: 2, md: 3 },
        pb: { xs: 8, md: 10 },
        maxWidth: 900,
        mx: "auto",
      }}
    >
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && payload && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
            Weak areas by subject
          </Typography>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            School : {schoolTitle}
          </Typography>
          {headerStudent && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {headerStudent}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Topics and subtopics where assignment scores are below {thresholdLabel}% for this school.
            {/* Only assignments linked to this target school (or not tied to a specific school) count. */}
          </Typography>

          {Array.isArray(payload.subjects) && payload.subjects.length === 0 && (
            <Alert severity="info">No subjects found for this student.</Alert>
          )}

          {Array.isArray(payload.subjects) &&
            payload.subjects.map((subject) => {
              const weak = subject.weak_areas || [];
              const count = weak.length;
              const topicGroups = groupWeakAreasByTopic(weak);
              const hasWeak = count > 0;

              return (
                <Accordion
                  key={subject.id}
                  defaultExpanded={hasWeak}
                  disableGutters
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    borderRadius: "12px !important",
                    border: 1,
                    borderColor: "divider",
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 56 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", pr: 1 }}>
                      <Typography fontWeight={700}>{subject.name}</Typography>
                      {hasWeak ? (
                        <Chip size="small" label={`${count} weak`} color="warning" variant="outlined" />
                      ) : (
                        <Chip size="small" label="On track" color="success" variant="outlined" />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                    {!hasWeak ? (
                      <Typography variant="body2" color="text.secondary">
                        No weak areas below {thresholdLabel}% for this school.
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {topicGroups.map((group, gi) => (
                          <Paper key={group.topicId || gi} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                                columnGap: 1.5,
                                rowGap: 0.5,
                                width: "100%",
                                mb: group.topicLevel.some((w) => w.suggestion) || group.subtopics.length ? 1 : 0,
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight={700} component="span">
                                Topic : {group.topicName}
                              </Typography>
                              {group.topicLevel.map((w, i) => (
                                <Chip
                                  key={`tl-${w.course_topic_id}-${i}`}
                                  size="small"
                                  label={`${Math.round(Number(w.score) || 0)}%`}
                                  color={scoreChipColor(w.score)}
                                  variant="outlined"
                                />
                              ))}
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label="View wrong questions for this topic"
                                title="View wrong questions"
                                onClick={() => openWrongQuestionsReview(subject, group, null)}
                                sx={{ ml: { xs: 0, sm: "auto" } }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            {group.topicLevel.map((w, i) =>
                              w.suggestion ? (
                                <Typography
                                  key={`tls-${w.course_topic_id}-${i}`}
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mb: group.subtopics.length ? 1 : 0 }}
                                >
                                  {w.suggestion}
                                </Typography>
                              ) : null
                            )}

                            {group.subtopics.length > 0 && (
                              <List dense disablePadding sx={{ mt: group.topicLevel.length ? 0.5 : 0 }}>
                                {group.subtopics.map((w, i) => (
                                  <ListItem
                                    key={`st-${w.course_sub_topic_id}-${i}`}
                                    sx={{ px: 0, py: 0.5, alignItems: "flex-start", pl: 1, borderLeft: 2, borderColor: "divider" }}
                                  >
                                    <ListItemText
                                      primaryTypographyProps={{ component: "div" }}
                                      secondaryTypographyProps={{ component: "div" }}
                                      primary={
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: 1,
                                            columnGap: 1.5,
                                            width: "100%",
                                          }}
                                        >
                                          <Typography variant="body2" fontWeight={500} component="span">
                                            {w.course_sub_topic_name || w.display_path || "Subtopic"}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            label={`${Math.round(Number(w.score) || 0)}%`}
                                            color={scoreChipColor(w.score)}
                                            variant="outlined"
                                          />
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="View wrong questions for this subtopic"
                                            title="View wrong questions"
                                            onClick={() => openWrongQuestionsReview(subject, group, w)}
                                            sx={{ ml: "auto" }}
                                          >
                                            <VisibilityIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      }
                                      secondary={
                                        w.suggestion ? (
                                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                            {w.suggestion}
                                          </Typography>
                                        ) : null
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </>
      )}

      <ParentWrongQuestionsReviewDialog
        open={reviewOpen}
        onClose={() => {
          setReviewOpen(false);
          setReviewError("");
          setReviewData(null);
        }}
        loading={reviewLoading}
        error={reviewError}
        viewData={reviewData}
        contextSubtitle={reviewContextSubtitle}
      />
    </Box>
  );
};

export default SchoolReadinessDetail;
